import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  Alert,
  AlertTitle,
  Breadcrumbs,
  Link,
  Divider,
  InputAdornment,
  FormHelperText,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import { trpc } from '../../lib/trpc';

/**
 * Color picker component
 */
function ColorPicker({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (color: string) => void;
  label: string;
}) {
  const presetColors = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#8B5CF6', // Purple
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#EC4899', // Pink
    '#06B6D4', // Cyan
    '#6366F1', // Indigo
    '#84CC16', // Lime
    '#F97316', // Orange
  ];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {presetColors.map((color) => (
          <Box
            key={color}
            onClick={() => onChange(color)}
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              backgroundColor: color,
              cursor: 'pointer',
              border: value === color ? 3 : 1,
              borderColor: value === color ? 'primary.main' : 'divider',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#3B82F6"
          sx={{ width: 120 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: 0.5,
                    backgroundColor: value,
                    border: 1,
                    borderColor: 'divider',
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary">
          Custom hex color
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Create status page component
 * Form for creating a new status page
 */
export function CreateStatusPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [selectedMonitors, setSelectedMonitors] = useState<string[]>([]);
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch user's monitors
  const monitorsQuery = trpc.monitors.list.useQuery();

  // Fetch plan limits
  const limitsQuery = trpc.statusPages.limits.useQuery();

  // Create mutation
  const createMutation = trpc.statusPages.create.useMutation({
    onSuccess: (data) => {
      // Invalidate queries to refresh data
      utils.statusPages.list.invalidate();
      utils.statusPages.limits.invalidate();

      navigate(`/status-pages/${data.id}`, {
        state: { message: 'Status page created successfully!' },
      });
    },
    onError: (error) => {
      setErrors((prev) => ({ ...prev, form: error.message }));
    },
  });

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    if (description && description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    if (logoUrl && !/^https?:\/\/.+/.test(logoUrl)) {
      newErrors.logoUrl = 'Logo URL must be a valid URL';
    }

    if (primaryColor && !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(primaryColor)) {
      newErrors.primaryColor = 'Primary color must be a valid hex color';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    createMutation.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
      isPublic,
      monitors: selectedMonitors.map((monitorId, index) => ({
        monitorId,
        order: index,
      })),
      branding: {
        logoUrl: logoUrl.trim() || undefined,
        primaryColor: primaryColor || undefined,
      },
    });
  };

  // Toggle monitor selection
  const toggleMonitor = (monitorId: string) => {
    setSelectedMonitors((prev) =>
      prev.includes(monitorId) ? prev.filter((id) => id !== monitorId) : [...prev, monitorId]
    );
  };

  // Check if user can create
  // Default to false when data isn't available to prevent accidental creation
  const canCreate = limitsQuery.data
    ? limitsQuery.data.limit === -1 ||
      limitsQuery.data.limit === Infinity ||
      limitsQuery.data.current < limitsQuery.data.limit
    : false;

  // Show error if limits query failed
  if (limitsQuery.isError) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/status-pages" color="inherit" underline="hover">
            Status Pages
          </Link>
          <Typography color="text.primary">Create</Typography>
        </Breadcrumbs>

        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          Unable to verify plan limits. Please try again later.
        </Alert>

        <Button
          component={RouterLink}
          to="/status-pages"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
        >
          Back to Status Pages
        </Button>
      </Box>
    );
  }

  if (!canCreate) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/status-pages" color="inherit" underline="hover">
            Status Pages
          </Link>
          <Typography color="text.primary">Create</Typography>
        </Breadcrumbs>

        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Limit Reached</AlertTitle>
          You have reached your status page limit. Please upgrade your plan to create more status
          pages.
        </Alert>

        <Button
          component={RouterLink}
          to="/status-pages"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
        >
          Back to Status Pages
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/status-pages" color="inherit" underline="hover">
          Status Pages
        </Link>
        <Typography color="text.primary">Create</Typography>
      </Breadcrumbs>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Create Status Page
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Create a public status page to keep your users informed
      </Typography>

      {/* Error Alert */}
      {createMutation.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error creating status page</AlertTitle>
          {errors.form || createMutation.error.message}
        </Alert>
      )}

      <Paper sx={{ p: 4, maxWidth: 800, mb: 3 }}>
        {/* Basic Info Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Basic Information
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Name */}
          <TextField
            label="Page Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name || 'The name displayed on your status page'}
            required
            fullWidth
          />

          {/* Description */}
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={!!errors.description}
            helperText={errors.description || 'Optional description shown on the status page'}
            multiline
            rows={3}
            fullWidth
          />

          {/* Public Toggle */}
          <FormControlLabel
            control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body1">Public Status Page</Typography>
                <Typography variant="body2" color="text.secondary">
                  {isPublic
                    ? 'Anyone with the link can view this status page'
                    : 'This status page is private and only visible to you'}
                </Typography>
              </Box>
            }
          />
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Monitors Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Monitors
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select which monitors to display on this status page
        </Typography>

        {monitorsQuery.isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            ))}
          </Box>
        ) : monitorsQuery.data && monitorsQuery.data.length > 0 ? (
          <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
            <List dense disablePadding>
              {monitorsQuery.data.map((monitor) => (
                <ListItem key={monitor.id} disablePadding divider>
                  <ListItemButton onClick={() => toggleMonitor(monitor.id)}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedMonitors.includes(monitor.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={monitor.name}
                      secondary={monitor.url}
                      primaryTypographyProps={{ fontWeight: 500 }}
                      secondaryTypographyProps={{
                        noWrap: true,
                        sx: { maxWidth: 400 },
                      }}
                    />
                    <Chip
                      label={monitor.status}
                      size="small"
                      color={
                        monitor.status === 'UP'
                          ? 'success'
                          : monitor.status === 'DOWN'
                            ? 'error'
                            : 'default'
                      }
                      variant="outlined"
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        ) : (
          <Paper
            variant="outlined"
            sx={{ p: 3, textAlign: 'center', backgroundColor: 'action.hover' }}
          >
            <MonitorHeartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No monitors available. Create monitors first to add them to your status page.
            </Typography>
            <Button
              component={RouterLink}
              to="/monitors/new"
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
            >
              Create Monitor
            </Button>
          </Paper>
        )}

        {selectedMonitors.length > 0 && (
          <FormHelperText sx={{ mt: 1 }}>
            {selectedMonitors.length} monitor{selectedMonitors.length !== 1 ? 's' : ''} selected
          </FormHelperText>
        )}

        <Divider sx={{ my: 4 }} />

        {/* Branding Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Branding
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Customize the look of your status page
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Logo URL */}
          <TextField
            label="Logo URL"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            error={!!errors.logoUrl}
            helperText={errors.logoUrl || 'URL to your logo image (recommended: 200x50px)'}
            placeholder="https://example.com/logo.png"
            fullWidth
          />

          {/* Primary Color */}
          <ColorPicker value={primaryColor} onChange={setPrimaryColor} label="Primary Color" />
          {errors.primaryColor && <FormHelperText error>{errors.primaryColor}</FormHelperText>}
        </Box>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          component={RouterLink}
          to="/status-pages"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={createMutation.isLoading}
        >
          {createMutation.isLoading ? 'Creating...' : 'Create Status Page'}
        </Button>
      </Box>
    </Box>
  );
}

export default CreateStatusPage;
